import { useCallback, useState } from "react";
import useFetchData from "./useFetch";
import { Token } from "../types/api";
import useDebounce from "./useDebounce";
import { DefaultSearchBounceInterval } from "../utils/constants/CommonConstants";

interface UsePaginatedFetchDataParams {
  queryKey: string;
  endpoint: string;
  token: Token;
  queryParams?: Record<string, any>;
  getSearchTermValue?: (val: string) => void;
  searchParam?: string;
  enabled?: boolean;
  navigateToErrorPageIfThereisError?: boolean;
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
}

const usePaginatedFetchData = ({
  queryKey,
  endpoint,
  token,
  queryParams,
  getSearchTermValue,
  searchParam = "",
  enabled,
  navigateToErrorPageIfThereisError,
  cacheTime,
  refetchOnMount,
  refetchOnWindowFocus,
  staleTime,
}: UsePaginatedFetchDataParams) => {
  const [searchTerm, setSearchTerm] = useState<string>(searchParam);
  const debouncedSearchParam = useDebounce(
    searchTerm,
    DefaultSearchBounceInterval,
  );

  const handleSearch = useCallback(
    (newSearchTerm: string) => {
      setSearchTerm(newSearchTerm);
      if (getSearchTermValue) getSearchTermValue(newSearchTerm);
    },
    [getSearchTermValue],
  );

  const dynamicQueryKey = [queryKey, queryParams, debouncedSearchParam];

  const { data, isLoading, error, refetch } = useFetchData<any>(
    dynamicQueryKey,
    endpoint,
    {
      token,
      queryParams,
      enabled,
      navigateToErrorPageIfThereisError,
      cacheTime,
      refetchOnMount,
      refetchOnWindowFocus,
      staleTime,
    },
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    handleSearch,
  };
};

export default usePaginatedFetchData;
